"use client";

import { Button } from "@/components/ui/button";
import { Link } from '@/navigation';
import { MapPin, Clock, Phone, CalendarClock, Building2 } from "lucide-react";
import { AnimatedSection } from "@/components/ui/animated-section";
import { useLocale, useTranslations } from 'next-intl';
import { LocationBenefits } from "@/components/locations/LocationBenefits";
import { LocationTestimonials } from "@/components/locations/LocationTestimonials";
import { LocationFAQ } from "@/components/locations/LocationFAQ";
import { LocationProductLinks } from "@/components/locations/LocationProductLinks";
import CakesGrid from "@/components/CakesGrid";
import type { SignatureCake } from "@/lib/signatureCakes";

interface VlaardingenClientProps {
  locale: string;
  signatureCakes: SignatureCake[];
  categoryOrder: Record<string, number>;
  isLegacyWebsiteEnabled?: boolean;
}

export function VlaardingenClient({ locale, signatureCakes, categoryOrder, isLegacyWebsiteEnabled = false }: VlaardingenClientProps) {
  const currentLocale = useLocale();
  const t = useTranslations('locations.vlaardingen');

  return (
    <>
      {/* Hero Section */}
      <section className="relative pt-20 pb-12 md:pt-32 md:pb-24">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fade-up" delay={0}>
            <div className="text-left">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight">
                {t('hero.title')}
              </h1>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-muted-foreground mb-2 md:mb-4 max-w-3xl">
                {t('hero.subtitle')}
              </p>
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-6 md:mb-8 max-w-3xl">
                {t('hero.description')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-start">
                <Button asChild size="lg" className="text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto min-h-[44px]">
                  <Link href="/order">
                    {t('hero.ctaPrimary')}
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto min-h-[44px]">
                  <Link href="/#cakes">
                    {t('hero.ctaSecondary')}
                  </Link>
                </Button>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Intro — unique Vlaardingen prose for content depth + keyword relevance */}
      <section className="py-8 md:py-12">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fade-up" delay={0}>
            <div className="max-w-3xl space-y-4 text-base md:text-lg text-muted-foreground leading-relaxed">
              {t.raw('intro.paragraphs').map((paragraph: string, index: number) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Location Info Section */}
      <section className="py-12 md:py-16 bg-muted/50">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <div>
            <AnimatedSection animation="fade-up" delay={0}>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 md:mb-8 text-left">
                {t('locationInfo.title')}
              </h2>
            </AnimatedSection>
            <div className="grid md:grid-cols-2 gap-6 md:gap-8">
              <div className="space-y-4 md:space-y-6">
                <AnimatedSection animation="fade-left" delay={100}>
                  <div className="bg-background p-4 md:p-6 border border-gray-200 hover:border-primary/30 transition-colors duration-200 group">
                    <div className="flex items-start gap-3 md:gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center transition-transform duration-200 group-hover:scale-110">
                          <MapPin className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-base md:text-lg mb-1.5 md:mb-2">
                          {t('locationInfo.location.title')}
                        </h3>
                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                          {t('locationInfo.location.description')}
                        </p>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
                <AnimatedSection animation="fade-left" delay={200}>
                  <div className="bg-background p-4 md:p-6 border border-gray-200 hover:border-primary/30 transition-colors duration-200 group">
                    <div className="flex items-start gap-3 md:gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center transition-transform duration-200 group-hover:scale-110">
                          <Clock className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-base md:text-lg mb-1.5 md:mb-2">
                          {t('locationInfo.pickupTimes.title')}
                        </h3>
                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                          {t('locationInfo.pickupTimes.description')}
                        </p>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
                <AnimatedSection animation="fade-left" delay={300}>
                  <div className="bg-background p-4 md:p-6 border border-gray-200 hover:border-primary/30 transition-colors duration-200 group">
                    <div className="flex items-start gap-3 md:gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center transition-transform duration-200 group-hover:scale-110">
                          <CalendarClock className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-base md:text-lg mb-1.5 md:mb-2">
                          {t('locationInfo.openingHours.title')}
                        </h3>
                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                          {t('locationInfo.openingHours.description')}
                        </p>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
                <AnimatedSection animation="fade-left" delay={400}>
                  <div className="bg-background p-4 md:p-6 border border-gray-200 hover:border-primary/30 transition-colors duration-200 group">
                    <div className="flex items-start gap-3 md:gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center transition-transform duration-200 group-hover:scale-110">
                          <Building2 className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-base md:text-lg mb-1.5 md:mb-2">
                          {t('locationInfo.neighborhood.title')}
                        </h3>
                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                          {t('locationInfo.neighborhood.description')}
                        </p>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
                <AnimatedSection animation="fade-left" delay={500}>
                  <div className="bg-background p-4 md:p-6 border border-gray-200 hover:border-primary/30 transition-colors duration-200 group">
                    <div className="flex items-start gap-3 md:gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center transition-transform duration-200 group-hover:scale-110">
                          <Phone className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-base md:text-lg mb-1.5 md:mb-2">
                          {t('locationInfo.contact.title')}
                        </h3>
                        <p className="text-sm md:text-base text-muted-foreground break-words">
                          {t('locationInfo.contact.email')}
                        </p>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              </div>
              <AnimatedSection animation="fade-right" delay={100}>
                <div className="space-y-3 md:space-y-4">
                  <h3 className="font-semibold text-base md:text-lg mb-3 md:mb-4">
                    {t('howItWorks.title')}
                  </h3>
                  <div className="space-y-3 md:space-y-4">
                    {t.raw('howItWorks.steps').map((step: any, index: number) => (
                      <AnimatedSection
                        key={index}
                        animation="fade-right"
                        delay={200 + index * 100}
                      >
                        <div className="flex items-start gap-3 group">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm transition-transform duration-200 group-hover:scale-110">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-sm md:text-base mb-1">{step.title}</h4>
                            <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                              {step.description}
                            </p>
                          </div>
                        </div>
                      </AnimatedSection>
                    ))}
                  </div>
                </div>
              </AnimatedSection>
            </div>
            
            {/* Service Area Section */}
            <AnimatedSection animation="fade-up" delay={600}>
              <div className="mt-8 md:mt-12 pt-8 border-t">
                <h3 className="font-semibold text-lg md:text-xl mb-4 text-left">
                  {t('serviceArea.title')}
                </h3>
                <p className="text-sm md:text-base text-muted-foreground text-left mb-4">
                  {t('serviceArea.description')}
                </p>
                <div className="flex flex-wrap justify-start gap-3 md:gap-4">
                  {t.raw('serviceArea.cities').map((city: string, index: number) => (
                    <AnimatedSection
                      key={city}
                      animation="scale"
                      delay={700 + index * 50}
                    >
                      <span className="px-4 py-2 bg-background rounded-full text-sm font-medium border hover:shadow-md transition-shadow duration-200">
                        {city}
                      </span>
                    </AnimatedSection>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Cakes by occasion — internal links to city×product landing pages */}
      <LocationProductLinks city="vlaardingen" />

      {/* Benefits Section */}
      <LocationBenefits location="vlaardingen" locale={locale} />

      {/* Testimonials Section */}
      <LocationTestimonials location="vlaardingen" locale={locale} />

      {/* FAQ Section */}
      <LocationFAQ location="vlaardingen" locale={locale} />

      {/* Cakes Grid Section */}
      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fade-up" delay={0}>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 md:mb-8 text-left">
              {t('cakes.title')}
            </h2>
          </AnimatedSection>
          <CakesGrid
            initialCakes={signatureCakes}
            initialCategoryOrder={categoryOrder}
            isLegacyWebsiteEnabled={isLegacyWebsiteEnabled}
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16 pb-24 md:pb-16 bg-primary text-primary-foreground">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8 text-left">
          <AnimatedSection animation="scale" delay={0}>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 md:mb-4">
              {t('cta.title')}
            </h2>
            <p className="text-base sm:text-lg md:text-xl mb-6 md:mb-8 opacity-90 max-w-3xl">
              {t('cta.description')}
            </p>
            <Button asChild size="lg" variant="secondary" className="text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 min-h-[44px]">
              <Link href="/order">
                {t('cta.button')}
              </Link>
            </Button>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
}
